import { ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, Type, } from '@angular/core';
import { AbstractControl, ControlContainer, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DynamicDialogRef } from 'primeng/dynamicdialog';

import { OverlayService } from "@core/overlay.service";
import { TradeItemDto } from '@app/user/marks/marks-store/marks-types';
import { BoxValueAccessor } from '@shared/box-value-accessor/BoxValueAccessor';
import { SearchProductPopupComponent } from '@shared/shared-popups/search-product-popup/search-product-popup.component';

@Component({
    selector: 'app-search-product',
	providers: [{
		provide: NG_VALUE_ACCESSOR,
		multi: true,
		useExisting: forwardRef((): Type<SearchProductComponent> => SearchProductComponent)
	}],
    templateUrl: './search-product.component.html',
    styleUrls: ['./search-product.component.scss']
})
export class SearchProductComponent extends BoxValueAccessor implements OnInit, OnDestroy {
	@Input() public formControlName?: string;
	@Input() public partyId?: number;
	@Input() public reset$?: Observable<void>;
	@Input() public set selectionFromList(value: boolean) {
		this.isValueFromList = value;
	}
	@Output() public selectedValue: EventEmitter<TradeItemDto> = new EventEmitter<TradeItemDto>();
	@Output() public setDisabledField: EventEmitter<void> = new EventEmitter<void>();
    public showError = false;
    public isValueFromList = false;
	
	private previousValue = "";
	private ref?: DynamicDialogRef;
	private control?: AbstractControl | null;
	private unsubscribe$$: Subject<void> = new Subject<void>();

    constructor(
		public controlContainer: ControlContainer,
		private overlayService: OverlayService,
		private cdr: ChangeDetectorRef,
	) {
        super();
		this.value = null;
    }

    public ngOnInit(): void {
		if (this.controlContainer && this.formControlName && this.controlContainer.control){
			this.control = this.controlContainer.control.get(this.formControlName);
		}

		this.control && this.control.statusChanges.pipe(takeUntil(this.unsubscribe$$)).subscribe(() => {
			this.showError = this.control && this.control.touched && this.control.invalid || false;
			this.cdr.detectChanges();
		});

		this.reset$?.pipe(takeUntil(this.unsubscribe$$)).subscribe(() => {
			this.setDisabledField?.next();
			this.resetField();
		});
    }

    public handInput(event: any): void {
		if (this.isValueFromList) {
			this.isValueFromList = false;
		}

		const pattern = /^[0-9]*$/;
		if (!pattern.test(event.target.value)) {
			event.target.value = this.previousValue;
		} else {
			this.previousValue = event.target.value;
		}

		this.writeValue(event.target.value);
		if (this.onChange) {
			this.onChange(event.target.value);
		}
		if (this.onTouched) {
			this.onTouched();
		}
	}

	public deleteItem(event: Event): void {
		event.stopPropagation();
		this.resetField();
	}

    public addProduct(): void {
		if (this.ref) {
			return;
		}

		this.ref = this.overlayService.dialog.open(SearchProductPopupComponent, {
			data: {
				partyId: this.partyId
			}
		});
		this.ref.onClose.pipe(takeUntil(this.unsubscribe$$)).subscribe((product: TradeItemDto) => {
			this.ref = undefined;
			if (!product) {
				return;
			}
			this.selectItem(product);
			this.cdr.detectChanges();
		});
	}

	public ngOnDestroy(): void {
		this.ref?.destroy();
		this.unsubscribe$$.next();
		this.unsubscribe$$.complete();
	}

	private selectItem(product: TradeItemDto): void {
		this.value = product.gtin;
		this.selectedValue.emit(product);
		this.isValueFromList = true;
		this.changeAndTouch();
		this.cdr.markForCheck();
	}

	private resetField(): void {
		this.value = null;
		this.isValueFromList = false;
		this.selectedValue.emit(undefined);
		this.changeAndTouch();
		this.cdr.markForCheck();
	}

	private changeAndTouch(): void {
		if (this.onChange) {
			this.onChange(this.value);
		}
		if (this.onTouched){
			this.onTouched();
		}
	}
}
